import { formatDateTime, parseDateTime } from 'utils/dates/date.utils';
import { displayMarineValue, hasMarineValue } from '../../../utils/marineForecast.utils';

const getTideTimeMinutes = (time) => {
  const match = String(time ?? '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!match) return Number.POSITIVE_INFINITY;

  const [, hourValue, minuteValue, period] = match;
  let hour = Number(hourValue) % 12;

  if (period.toUpperCase() === 'PM') hour += 12;

  return hour * 60 + Number(minuteValue);
};

const sortTidesByTime = (tides) => {
  return [...tides].sort(
    (firstTide, secondTide) =>
      getTideTimeMinutes(firstTide?.time) - getTideTimeMinutes(secondTide?.time),
  );
};

const normalizeTideForecast = (forecast) => {
  const tideEntries = Array.isArray(forecast?.tide) ? forecast.tide.filter(Boolean) : [];
  const highTides = sortTidesByTime(
    tideEntries.filter((tide) => String(tide?.type).toUpperCase() === 'HIGH'),
  );
  const lowTides = sortTidesByTime(
    tideEntries.filter((tide) => String(tide?.type).toUpperCase() === 'LOW'),
  );

  return {
    ...forecast,
    tideSets: [
      { id: 'set-one', high: highTides[0], low: lowTides[0] },
      { id: 'set-two', high: highTides[1], low: lowTides[1] },
    ],
  };
};

export const getTideTableLocations = (tideEntries = []) => {
  return tideEntries.filter(Boolean).map((entry, index) => {
    const locationName = displayMarineValue(entry?.location, 'Unnamed Tide Location');

    const forecasts = (entry?.forecast ?? [])
      .filter(Boolean)
      .map(normalizeTideForecast)
      .sort((firstForecast, secondForecast) => {
        const firstDate =
          parseDateTime(firstForecast?.iso8601)?.getTime() ?? Number.POSITIVE_INFINITY;
        const secondDate =
          parseDateTime(secondForecast?.iso8601)?.getTime() ?? Number.POSITIVE_INFINITY;

        return firstDate - secondDate;
      });

    return {
      locationKey: entry?.location ?? `tide-location-${index}`,
      locationName,
      forecasts,
    };
  });
};

export const getTideLocationSubtitle = (location) => {
  const dayCount = location?.forecasts?.length ?? 0;
  return `${dayCount} forecast ${dayCount === 1 ? 'day' : 'days'}`;
};

export const getTideForecastCardKey = (forecast, index) =>
  forecast?.iso8601 ?? `tide-forecast-${index}`;

export const getTideForecastCardTitle = (forecast) =>
  formatDateTime(forecast?.iso8601, 'long', 'Unknown forecast date', true);

export const formatTideEvent = (tide, fallbackType) => {
  if (!tide) return '--';

  const time = displayMarineValue(tide?.time);
  const rawHeight = displayMarineValue(tide?.height);
  const height =
    hasMarineValue(tide?.height) && !/\bft\b/i.test(String(rawHeight))
      ? `${rawHeight} ft`
      : rawHeight;
  const type = displayMarineValue(tide?.type, fallbackType);

  return `${time} ${height} ${type}`;
};
